import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaTimes, FaUpload, FaFile, FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaFolder, FaTrash } from 'react-icons/fa';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { storage, db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import {
  isArchivedAssignment,
  getArchivedAssignmentLabel,
} from '../../utils/assignmentHistory';
import styles from './FileUploadModal.module.css';

const EmployeeFileUploadModal = ({ onClose, project, employee }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [selectedCenterId, setSelectedCenterId] = useState('');
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (employee.centers && employee.centers.length > 0) {
      // Yeni yükleme varsayılan olarak hâlâ çalıştığı bir merkeze gitsin;
      // ayrıldığı merkezler sadece eski dosyaları görmek için listede kalır.
      const firstActive = employee.centers.find(center => !isArchivedAssignment(center));
      setSelectedCenterId((firstActive || employee.centers[0]).centerId);
    }
  }, [employee]);

  useEffect(() => {
    fetchUploadedFiles();
  }, [project.id, employee.id]);

  // Handle closing when clicking outside the modal
  const handleOverlayClick = useCallback((e) => {
    if (e.target === modalRef.current && !isUploading) {
      onClose();
    }
  }, [onClose, isUploading]);

  // Add escape key listener to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !isUploading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, isUploading]);

  // Get file icon based on file type
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();

    if (extension === 'pdf') return <FaFilePdf />;
    if (['doc', 'docx'].includes(extension)) return <FaFileWord />;
    if (['xls', 'xlsx', 'csv'].includes(extension)) return <FaFileExcel />;
    if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(extension)) return <FaFileImage />;
    return <FaFile />;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const fetchUploadedFiles = async () => {
    try {
      // Get the project document to fetch existing files for this employee
      const projectDoc = await getDoc(doc(db, 'Projects', project.id));
      const projectData = projectDoc.data();

      if (projectData.employees) {
        // Find all entries for this employee
        const employeeEntries = projectData.employees.filter(emp => emp.id === employee.id);

        // Collect all files from all entries
        const allFiles = [];
        employeeEntries.forEach(entry => {
          if (entry.files && Array.isArray(entry.files)) {
            allFiles.push(...entry.files.map(file => ({
              ...file,
              centerId: entry.centerId,
              centerName: entry.centerName
            })));
          }
        });

        setUploadedFiles(allFiles);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files.length > 0) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    setIsUploading(true);
    try {
      // Create a reference to the file in Firebase Storage
      const fileRef = ref(storage, `projects/${project.id}/employees/${employee.id}/centers/${selectedCenterId}/sunum/${file.name}`);

      // Upload the file
      await uploadBytes(fileRef, file);

      // Get the download URL
      const downloadURL = await getDownloadURL(fileRef);

      // Update the project document in Firestore
      const projectRef = doc(db, 'Projects', project.id);
      const newFile = {
        type: 'sunum',
        name: file.name,
        url: downloadURL,
        uploadedAt: new Date().toISOString(),
        storagePath: `projects/${project.id}/employees/${employee.id}/centers/${selectedCenterId}/sunum/${file.name}`
      };

      // Get project document
      const projectDoc = await getDoc(projectRef);
      const projectData = projectDoc.data();

      if (projectData.employees) {
        // Update only the specific entry for this employee and center
        const updatedEmployees = projectData.employees.map(emp => {
          if (emp.id === employee.id && emp.centerId === selectedCenterId) {
            const existingFiles = emp.files || [];
            return {
              ...emp,
              files: [...existingFiles, newFile]
            };
          }
          return emp;
        });

        await updateDoc(projectRef, {
          employees: updatedEmployees
        });
      }

      // Refresh the files list
      await fetchUploadedFiles();
      toast.success('Dosya başarıyla yüklendi');

    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Dosya yükleme hatası');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteFile = async (fileToDelete, index) => {
    if (!window.confirm(`"${fileToDelete.name}" dosyasını silmek istediğinizden emin misiniz?`)) {
      return;
    }

    setIsDeleting(index);
    try {
      // Delete from Firebase Storage
      const storagePath = fileToDelete.storagePath || `projects/${project.id}/employees/${employee.id}/sunum/${fileToDelete.name}`;
      const fileRef = ref(storage, storagePath);
      await deleteObject(fileRef);

      // Update Firestore document
      const projectRef = doc(db, 'Projects', project.id);
      const projectDoc = await getDoc(projectRef);
      const projectData = projectDoc.data();

      if (projectData.employees) {
        // Remove file from the specific entry
        const updatedEmployees = projectData.employees.map(emp => {
          // Check if this is the correct employee and center (if centerId is available in fileToDelete)
          // If fileToDelete doesn't have centerId (old files), we might need to check all entries or rely on storage path
          // But fetchUploadedFiles adds centerId to the file object, so we should be good.
          if (emp.id === employee.id && (fileToDelete.centerId ? emp.centerId === fileToDelete.centerId : true)) {
            const existingFiles = emp.files || [];
            const updatedFiles = existingFiles.filter(file => file.url !== fileToDelete.url);
            return {
              ...emp,
              files: updatedFiles
            };
          }
          return emp;
        });

        await updateDoc(projectRef, {
          employees: updatedEmployees
        });
      }

      // Refresh the files list
      await fetchUploadedFiles();
      toast.success('Dosya başarıyla silindi');
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Dosya silinirken hata oluştu: ' + error.message);
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <div
      className={styles.modalOverlay}
      ref={modalRef}
      onClick={handleOverlayClick}
    >
      <div className={styles.modalContent}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            Sunum Dosyası Yükleme - {employee.name} {employee.surname}
          </h2>
          <button
            onClick={onClose}
            className={styles.closeButton}
            disabled={isUploading}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Center Selection */}
          {employee.centers && employee.centers.length > 0 && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Merkez Seçimi</label>
              <select
                className={styles.select}
                value={selectedCenterId}
                onChange={(e) => setSelectedCenterId(e.target.value)}
              >
                {/* Ayrılınan merkezler de listelenir: o merkezdeki eski dosyalara
                    erişim sürsün diye kayıtları silinmiyor. */}
                {employee.centers.map(center => (
                  <option key={center.centerId} value={center.centerId}>
                    {center.centerName || center.centerId}
                    {isArchivedAssignment(center) ? ` — ${getArchivedAssignmentLabel(center)}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* File Upload Area */}
          <div
            className={styles.dropzone}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
          >
            <div className={styles.dropzoneIcon}>
              <FaUpload />
            </div>
            <p className={styles.dropzoneText}>Sunum Dosyalarınızı Sürükleyin</p>
            <p className={styles.dropzoneText}>veya</p>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className={styles.fileInput}
              disabled={isUploading}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                fileInputRef.current.click();
              }}
              disabled={isUploading}
              className={styles.uploadButton}
              type="button"
            >
              {isUploading ? (
                <>
                  <span className={styles.loadingSpinner}></span>
                  Yükleniyor...
                </>
              ) : (
                <>
                  <FaUpload />
                  Dosya Seçin
                </>
              )}
            </button>
          </div>

          {/* Uploaded Files List */}
          <div className={styles.fileListSection}>
            <h3 className={styles.fileListTitle}>
              <FaFolder style={{ marginRight: '0.5rem' }} />
              Yüklenmiş Sunum Dosyaları
            </h3>
            <div className={styles.fileList}>
              {uploadedFiles.length > 0 ? (
                uploadedFiles.map((file, index) => (
                  <div key={index} className={styles.fileItem}>
                    <div className={styles.fileType}>
                      Sunum
                      {file.centerName && (
                        <span style={{ fontSize: '0.8em', marginLeft: '0.5rem', opacity: 0.8 }}>
                          ({file.centerName})
                        </span>
                      )}
                    </div>
                    <div
                      className={styles.fileLink}
                      onClick={() => window.open(file.url, '_blank')}
                    >
                      {getFileIcon(file.name)}
                      {file.name}
                    </div>
                    <div className={styles.fileActions}>
                      {file.uploadedAt && (
                        <small style={{ color: 'var(--file-type)', fontSize: '0.7rem' }}>
                          {formatDate(file.uploadedAt)}
                        </small>
                      )}
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDeleteFile(file, index)}
                        disabled={isDeleting === index || isUploading}
                        title="Dosyayı Sil"
                      >
                        {isDeleting === index ? (
                          <span className={styles.loadingSpinner}></span>
                        ) : (
                          <FaTrash />
                        )}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.noFilesMessage}>
                  Henüz dosya yüklenmemiş
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeFileUploadModal;
